/**
 * One Extras attachment — a linked lesson or an external URL — §2.8 #90.
 *
 * **Why the card itself is not a button.** It used to be a `div` with
 * `role='button'` and `tabIndex={0}`, with the delete control as a descendant.
 * That is `nested-interactive`: an element in the accessibility tree exposed as
 * a button, containing another button. Screen readers cannot present it
 * coherently, and the card's accessible name swallowed the delete control's.
 *
 * The fix is the "stretched link" pattern: the TITLE is the real control, and
 * an `::after` pseudo-element on it covers the card so the whole surface stays
 * clickable for a pointer. The options button sits above that overlay
 * (`relative z-10`). Keyboard users get ordinary, separately-named buttons
 * instead of one button nested inside another; mouse users notice nothing.
 *
 * **Why Remove is behind ⋯.** A trash can on every row, one tap from deleting,
 * sat exactly where a thumb rests on a phone. Removing now takes a deliberate
 * second tap; opening stays one tap on the row.
 */
import { useRef, useState } from 'react';
import {
  BookOpen,
  ExternalLink,
  Link2,
  Loader2,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import type { ExtrasAttachment } from '@/lib/api-client';
import { useDismiss } from '@/hooks/ui/useDismiss';

export interface AttachmentCardProps {
  attachment: ExtrasAttachment;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
  isDeleting: boolean;
}

/** `example.test · Link`, or `Lesson in your library`. */
function metaLine(attachment: ExtrasAttachment): string {
  if (attachment.kind === 'lesson') return 'Lesson in your library';
  if (!attachment.url) return 'Link';
  try {
    return `${new URL(attachment.url).hostname.replace(/^www\./, '')} · Link`;
  } catch {
    return 'Link';
  }
}

export function AttachmentCard({
  attachment,
  onOpen,
  onDelete,
  isDeleting,
}: AttachmentCardProps) {
  const Icon = attachment.kind === 'lesson' ? BookOpen : Link2;

  return (
    <div className='group relative flex items-start gap-3 rounded-lg border border-border/60 p-3 transition-colors hover:border-border hover:bg-muted/40 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40 dark:hover:border-muted-foreground/40 max-sm:rounded-none max-sm:border-x-0 max-sm:border-t-0 max-sm:px-0 max-sm:hover:bg-transparent'>
      <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground'>
        <Icon className='h-[18px] w-[18px]' aria-hidden='true' />
      </span>
      <div className='min-w-0 flex-1'>
        <h4 className='truncate text-[15px] font-medium leading-snug text-foreground sm:text-sm'>
          {/* The stretched control: `after:absolute after:inset-0` keeps the
              whole card clickable now that the wrapper is inert. */}
          <button
            type='button'
            onClick={onOpen}
            className='block w-full truncate text-left after:absolute after:inset-0 after:content-[""] focus:outline-none'
          >
            {attachment.title}
          </button>
        </h4>
        {attachment.description && (
          <p className='mt-0.5 line-clamp-2 text-sm text-muted-foreground'>
            {attachment.description}
          </p>
        )}
        <p className='mt-1 truncate font-mono text-xs text-muted-foreground'>
          {metaLine(attachment)}
        </p>
      </div>
      <AttachmentMenu
        title={attachment.title}
        isLink={attachment.kind === 'link'}
        isDeleting={isDeleting}
        onOpen={onOpen}
        onDelete={onDelete}
      />
    </div>
  );
}

/** ⋯ - Open and Remove. Above the stretched title (`relative z-10`). */
function AttachmentMenu({
  title,
  isLink,
  isDeleting,
  onOpen,
  onDelete,
}: {
  title: string;
  isLink: boolean;
  isDeleting: boolean;
  onOpen: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, open, () => setOpen(false));

  const item =
    'flex w-full items-center gap-2 px-3 py-3 text-left text-sm transition-colors hover:bg-muted sm:py-2';

  return (
    <div ref={ref} className='relative z-10 flex-shrink-0'>
      <button
        type='button'
        onClick={() => setOpen(v => !v)}
        aria-label={`Options for ${title}`}
        aria-expanded={open}
        disabled={isDeleting}
        className='tap-target flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50'
      >
        {isDeleting ? (
          <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
        ) : (
          <MoreHorizontal className='h-4 w-4' aria-hidden='true' />
        )}
      </button>
      {open && (
        <div className='absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden rounded-lg border border-border bg-card py-1 shadow-elevation-2'>
          <button
            type='button'
            className={`${item} text-foreground`}
            onClick={() => {
              setOpen(false);
              onOpen();
            }}
          >
            {isLink ? (
              <ExternalLink className='h-4 w-4' aria-hidden='true' />
            ) : (
              <BookOpen className='h-4 w-4' aria-hidden='true' />
            )}
            Open
          </button>
          <button
            type='button'
            aria-label={`Remove ${title}`}
            className={`${item} text-destructive-emphasis`}
            onClick={e => {
              setOpen(false);
              onDelete(e);
            }}
          >
            <Trash2 className='h-4 w-4' aria-hidden='true' />
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
